import * as React from 'react';

/**
 * Acts exactly like React.useState, but calls the provided callback whenever the value changes.
 * The parameters of the callback is the updated value.
 * The callback does not need to be memoized, as internally a ref is used.
 *
 * @example
 * const [value, setValue] = useChange('', val => console.log(val));
 * <input type="text" value={value} onChange={event => setValue(event.target.value)} />
 * @param initialValue - Initial value to monitor
 * @param callback -  - The callback to be invoked when the value changes.  The single parameter is the updated value.
 * @returns - A tuple, the first index is the value itself, the second is a setValue, exactly like React.useState.
 */
export function useChange<T>(
    initialValue: T,
    callback: (value: T) => void,
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [value, setValue] = React.useState(initialValue);
    const callbackRef = React.useRef<(value: T) => void>();

    React.useEffect(() => {
        callbackRef.current = callback;
    });

    React.useEffect(() => {
        if (callbackRef.current) {
            callbackRef.current(value);
        }
    }, [value]);

    return [value, setValue];
}
